import { crewListBirthText } from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Group,
  SegmentedControl,
  Select,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowUp,
  IconDownload,
  IconListDetails,
  IconPlus,
  IconPrinter,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from '../drills/DrillPlanner.module.css';
import { browserPdfOutput, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import table from '../pms/Pms.module.css';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import own from './CrewLists.module.css';
import {
  type CrewListPdfT,
  crewListDocumentLines,
  crewListFileName,
  crewListName,
  crewListPdf,
} from './crew-list-pdf.js';
import {
  type CardField,
  type EditorHeader,
  type EditorRow,
  type EditorState,
  editedFromCard,
  initialEditor,
  moveRow,
  saveInput,
  selectedDocs,
  sortRows,
  withDocNumber,
  withDocSelection,
  withDocType,
  withVoyage,
} from './editor.js';
import type {
  CrewListDirection,
  CrewListDocType,
  CrewListDraft,
  CrewListSource,
  CrewLists,
  CrewListView,
} from './types.js';

export interface CrewListsScreenProps {
  source: CrewListSource;
}

type Load<T> = { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: T };

/** Kutu 11 belge türleri, ekrandaki sırayla (pasaport, cüzdan). */
const DOC_TYPES: readonly CrewListDocType[] = ['passport', 'seaman_book'];

/** Ekran hatasının metni: bilinen kodlar kendi metniyle, diğerleri genel. */
function useErrorText() {
  const { t } = useTranslation();
  return (code: string) =>
    t(`crewList.errors.${code}`, { defaultValue: t('crewList.errors.failed') });
}

/** Çeviri işlevi PDF'in beklediği biçimde. */
function usePdfT(): CrewListPdfT {
  const { t } = useTranslation();
  return (key, options) => t(key, options ?? {});
}

/** PDF indir ve yazdır: gemide uygulamanın yolu, web'de tarayıcı. */
function useCrewListPdf(source: CrewListSource) {
  const t = usePdfT();
  return {
    async save(list: CrewListView) {
      const output = source.pdf ?? browserPdfOutput();
      await output.save(crewListFileName(list), () => renderPdf(crewListPdf(t, list)));
    },
    async print(list: CrewListView) {
      const output = source.pdf ?? browserPdfOutput();
      await output.print(crewListFileName(list), () =>
        renderPdf(crewListPdf(t, list), { print: true }),
      );
    },
  };
}

/**
 * Crew ▸ Crew Lists (IMO FAL Form 5; ortak 2026-10-26 madde 4; ADR 0130 §B). Üç görünüm, adres
 * sorgusuyla: liste geçmişi, `?new=1` yeni liste, `?list=<id>` kaydedilmiş liste. Ofiste gemi
 * `?vessel=<id>` ile seçilir. Kaydedilen liste değişmez; PDF düz biçimdedir (şirket üst ve alt
 * bilgisi yok).
 */
export function CrewListsScreen({ source }: CrewListsScreenProps) {
  const { t } = useTranslation();
  const [query, setQuery] = usePmsQuery();
  const vesselId = source.scope === 'office' ? query.get('vessel') : null;
  const listId = query.get('list');
  const creating = query.get('new') === '1';
  const [state, setState] = useState<Load<CrewLists>>({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await source.list(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);
  const header = <PageHeader title={t('menu.crewLists')} description={t('crewList.description')} />;
  if (state.kind === 'loading') {
    return (
      <div className={table.screen}>
        {header}
        <TableSkeleton columns={8} label={t('menu.crewLists')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={table.screen}>
        {header}
        <Panel>
          <LoadError code={state.code} title={t('crewList.error')} onRetry={() => void load()} />
        </Panel>
      </div>
    );
  }
  const draftVessel = source.scope === 'ship' ? (state.data.vessels[0]?.id ?? null) : vesselId;
  const back = () => setQuery({ new: null, list: null });
  return (
    <div className={table.screen}>
      {header}
      {creating && draftVessel ? (
        <CrewListEditor
          source={source}
          vesselId={draftVessel}
          onCancel={back}
          onSaved={async (saved) => {
            await load();
            setQuery({ new: null, list: saved.id });
          }}
        />
      ) : listId ? (
        <CrewListDetailView source={source} id={listId} onBack={back} />
      ) : (
        <CrewListsBody
          data={state.data}
          vesselId={vesselId}
          onVessel={(id) => setQuery({ vessel: id })}
          onNew={() => setQuery({ new: '1', list: null })}
          onOpen={(id) => setQuery({ list: id, new: null })}
        />
      )}
    </div>
  );
}

/** Liste geçmişi (ekran testleri doğrudan çizer). */
export function CrewListsBody({
  data,
  vesselId,
  onVessel,
  onNew,
  onOpen,
}: {
  data: CrewLists;
  vesselId: string | null;
  onVessel: (id: string | null) => void;
  onNew: () => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const c = (key: string) => t(`crewList.columns.${key}`);
  const office = data.side === 'office';
  // Ofiste yeni liste için gemi seçilmeli (tek gemi varsa o)
  const canNew = data.can.create && (!office || vesselId !== null);
  return (
    <>
      <Panel>
        <div className={classes.bar}>
          {office ? (
            <Select
              aria-label={t('crewList.vessel')}
              placeholder={t('crewList.allVessels')}
              clearable
              data={data.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={vesselId}
              onChange={onVessel}
              maw={260}
            />
          ) : (
            <span />
          )}
          {data.can.create ? (
            <Button leftSection={<IconPlus size={16} />} onClick={onNew} disabled={!canNew}>
              {t('crewList.newList')}
            </Button>
          ) : null}
        </div>
        {office && data.can.create && !vesselId ? (
          <div className={classes.muted}>{t('crewList.chooseVessel')}</div>
        ) : null}
      </Panel>
      <Panel>
        {data.lists.length === 0 ? (
          <EmptyState icon={<IconListDetails size={22} />} title={t('crewList.empty')} />
        ) : (
          <div className={table.tableBox}>
            <table className={table.table} data-cards data-testid="crew-lists">
              <thead>
                <tr>
                  <th scope="col">{c('created')}</th>
                  {office ? <th scope="col">{c('vessel')}</th> : null}
                  <th scope="col">{c('direction')}</th>
                  <th scope="col">{c('port')}</th>
                  <th scope="col">{c('date')}</th>
                  <th scope="col">{c('voyage')}</th>
                  <th scope="col" data-numeric>
                    {c('crew')}
                  </th>
                  <th scope="col">{c('source')}</th>
                  <th scope="col">
                    <VisuallyHidden>{c('actions')}</VisuallyHidden>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.lists.map((list) => (
                  <tr key={list.id}>
                    <td data-label={c('created')}>
                      {formatDateTime(list.createdAt)}
                      <div className={classes.muted}>{list.createdByName ?? '—'}</div>
                    </td>
                    {office ? <td data-label={c('vessel')}>{list.vesselName}</td> : null}
                    <td data-label={c('direction')}>{t(`crewList.direction.${list.direction}`)}</td>
                    <td data-label={c('port')}>{list.port ?? '—'}</td>
                    <td data-label={c('date')}>{formatDay(list.listDate)}</td>
                    <td data-label={c('voyage')}>{list.voyageNo ?? '—'}</td>
                    <td data-label={c('crew')} data-numeric>
                      {list.crewCount}
                    </td>
                    <td data-label={c('source')}>
                      <StatusBadge tone={list.origin === 'ship' ? 'info' : 'neutral'}>
                        {t(`crewList.origin.${list.origin}`)}
                      </StatusBadge>
                    </td>
                    <td>
                      <Button size="xs" variant="light" onClick={() => onOpen(list.id)}>
                        {t('crewList.actions.pdf')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

/** Kaydedilmiş liste: bilgiler, satırlar, PDF ve yazdırma. */
function CrewListDetailView({
  source,
  id,
  onBack,
}: {
  source: CrewListSource;
  id: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [state, setState] = useState<Load<CrewListView>>({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await source.detail(id) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, id]);
  useEffect(() => {
    void load();
  }, [load]);
  if (state.kind === 'loading') return <TableSkeleton columns={6} label={t('menu.crewLists')} />;
  if (state.kind === 'error') {
    return (
      <Panel>
        <LoadError code={state.code} title={t('crewList.error')} onRetry={() => void load()} />
      </Panel>
    );
  }
  return (
    <CrewListDetailBody list={state.data} source={source} onBack={onBack} errorText={errorText} />
  );
}

/** Kaydedilmiş listenin gövdesi (ekran testleri doğrudan çizer). */
export function CrewListDetailBody({
  list,
  source,
  onBack,
  errorText,
}: {
  list: CrewListView;
  source: CrewListSource;
  onBack: () => void;
  errorText: (code: string) => string;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`crewList.fields.${key}`);
  const pt = usePdfT();
  const pdf = useCrewListPdf(source);
  const [error, setError] = useState<string | null>(null);
  const run = (action: () => Promise<void>) => async () => {
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(errorText(errorCode(cause)));
    }
  };
  const h = list.header;
  const facts: [string, string][] = [
    [f('vesselName'), h.vesselName],
    [f('imo'), h.imo ?? ''],
    [f('callSign'), h.callSign ?? ''],
    [f('voyageNo'), h.voyageNo ?? ''],
    [f('port'), h.port ?? ''],
    [f('date'), h.date ? formatDay(h.date) : ''],
    [f('flag'), h.flag ?? ''],
    [f('lastPort'), h.lastPort ?? ''],
  ];
  return (
    <>
      <Panel>
        <div className={classes.bar}>
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            {t('crewList.actions.back')}
          </Button>
          <Group gap="xs">
            <Button leftSection={<IconDownload size={16} />} onClick={run(() => pdf.save(list))}>
              {t('crewList.actions.pdf')}
            </Button>
            <Button
              variant="light"
              leftSection={<IconPrinter size={16} />}
              onClick={run(() => pdf.print(list))}
            >
              {t('crewList.actions.print')}
            </Button>
          </Group>
        </div>
        {error ? (
          <Alert color="red" mb="sm">
            {error}
          </Alert>
        ) : null}
        <div className={classes.muted}>
          {t(`crewList.direction.${list.direction}`)} · {t(`crewList.origin.${list.origin}`)} ·{' '}
          {formatDateTime(list.createdAt)} · {list.createdByName ?? '—'}
        </div>
        <dl className={own.facts} data-testid="crew-list-header">
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value || '—'}</dd>
            </div>
          ))}
        </dl>
      </Panel>
      <Panel>
        <div className={table.tableBox}>
          <table className={table.table} data-cards data-testid="crew-list-rows">
            <thead>
              <tr>
                <th scope="col">{f('no')}</th>
                <th scope="col">{f('name')}</th>
                <th scope="col">{f('rank')}</th>
                <th scope="col">{f('nationality')}</th>
                <th scope="col">{f('birth')}</th>
                <th scope="col">{f('document')}</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((row, index) => (
                <tr key={row.userId}>
                  <td data-label={f('no')}>{index + 1}</td>
                  <td data-label={f('name')}>{crewListName(row)}</td>
                  <td data-label={f('rank')}>{row.rank ?? '—'}</td>
                  <td data-label={f('nationality')}>{row.nationality ?? '—'}</td>
                  <td data-label={f('birth')}>
                    {crewListBirthText(row.birthDate, row.birthPlace, formatDay) || '—'}
                  </td>
                  <td data-label={f('document')}>
                    {/* Kutu 11: belgeler alt alta (04.11 notları, karar 13) */}
                    {crewListDocumentLines(pt, row).map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

/** Yeni liste: taslağı yükler, düzenleyiciyi çizer. */
function CrewListEditor({
  source,
  vesselId,
  onCancel,
  onSaved,
}: {
  source: CrewListSource;
  vesselId: string;
  onCancel: () => void;
  onSaved: (list: CrewListView) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const errorText = useErrorText();
  const pdf = useCrewListPdf(source);
  const [draft, setDraft] = useState<Load<CrewListDraft>>({ kind: 'loading' });
  const [editor, setEditor] = useState<EditorState | null>(null);
  const load = useCallback(async () => {
    try {
      const data = await source.draft(vesselId);
      setDraft({ kind: 'ready', data });
      setEditor(initialEditor(data, i18n.language));
    } catch (error) {
      setDraft({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId, i18n.language]);
  useEffect(() => {
    void load();
  }, [load]);
  if (draft.kind === 'loading' || (draft.kind === 'ready' && !editor)) {
    return <TableSkeleton columns={6} label={t('crewList.editor.title')} />;
  }
  if (draft.kind === 'error' || !editor) {
    return (
      <Panel>
        <LoadError
          code={draft.kind === 'error' ? draft.code : 'UNKNOWN'}
          title={t('crewList.error')}
          onRetry={() => void load()}
        />
      </Panel>
    );
  }
  return (
    <CrewListEditorBody
      draft={draft.data}
      state={editor}
      onChange={setEditor}
      onCancel={onCancel}
      onSave={async (print) => {
        const saved = await source.create(vesselId, saveInput(editor));
        // Kayıt tamam; PDF açılamasa da liste geçmişte kalır
        try {
          await (print ? pdf.print(saved) : pdf.save(saved));
        } finally {
          await onSaved(saved);
        }
      }}
      errorText={errorText}
    />
  );
}

/** Düzenleyicinin gövdesi (ekran testleri doğrudan çizer). */
export function CrewListEditorBody({
  draft,
  state,
  onChange,
  onCancel,
  onSave,
  errorText,
}: {
  draft: CrewListDraft;
  state: EditorState;
  onChange: (state: EditorState) => void;
  onCancel: () => void;
  onSave: (print: boolean) => Promise<void>;
  errorText: (code: string) => string;
}) {
  const { t, i18n } = useTranslation();
  const e = (key: string, values: Record<string, unknown> = {}) =>
    t(`crewList.editor.${key}`, values);
  const f = (key: string) => t(`crewList.fields.${key}`);
  const a = (key: string, values: Record<string, unknown> = {}) =>
    t(`crewList.actions.${key}`, values);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = state.rows.filter((row) => row.include).length;
  const setHeader = (patch: Partial<EditorHeader>) =>
    onChange({ ...state, header: { ...state.header, ...patch } });
  const setRow = (index: number, row: EditorRow) =>
    onChange({ ...state, rows: state.rows.map((r, i) => (i === index ? row : r)) });
  // Personel kartından gelen değer bu listede değiştirildi (ADR 0131 §2); kart değişmez
  const edited = (row: EditorRow, field: CardField) => {
    if (!editedFromCard(row).includes(field)) return null;
    const value = row.card?.[field] ?? '';
    const card = e('cardValue', { value: field === 'birthDate' ? formatDay(value) : value });
    return (
      <span className={own.edited} title={card}>
        {e('edited')}
        <span className={own.hidden}> · {card}</span>
      </span>
    );
  };
  const docTypes = (['passport', 'seaman_book'] as const).map((value) => ({
    value,
    label: t(`crewList.docType.${value}`),
  }));
  const save = async (print: boolean) => {
    setError(null);
    if (selected === 0) {
      setError(e('errors.noneSelected'));
      return;
    }
    setBusy(true);
    try {
      await onSave(print);
    } catch (cause) {
      setError(errorText(errorCode(cause)));
    } finally {
      setBusy(false);
    }
  };
  const text = (key: keyof EditorHeader, required = false) => (
    <TextInput
      label={f(key)}
      value={(state.header[key] as string | null) ?? ''}
      required={required}
      onChange={(event) => setHeader({ [key]: event.currentTarget.value })}
    />
  );
  const allSelected = selected === state.rows.length && state.rows.length > 0;
  return (
    <>
      <Panel>
        <div className={classes.bar}>
          <strong>{e('header')}</strong>
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onCancel}>
            {a('cancel')}
          </Button>
        </div>
        <div className={classes.muted}>{e('headerHint')}</div>
        <div className={classes.filters} data-testid="crew-list-editor-header">
          <SegmentedControl
            aria-label={e('direction')}
            value={state.direction}
            onChange={(value) =>
              onChange(withVoyage(state, draft, state.voyageId, value as CrewListDirection))
            }
            data={(['arrival', 'departure'] as const).map((value) => ({
              value,
              label: t(`crewList.direction.${value}`),
            }))}
          />
          <Select
            label={e('voyage')}
            placeholder={e('noVoyage')}
            clearable
            data={draft.voyages.map((v) => ({
              value: v.id,
              label: [v.voyageNo, [v.fromPort, v.toPort].filter(Boolean).join(' → ')]
                .filter(Boolean)
                .join(' · '),
            }))}
            value={state.voyageId}
            onChange={(id) => onChange(withVoyage(state, draft, id, state.direction))}
          />
          {text('vesselName', true)}
          {text('imo')}
          {text('callSign')}
          {text('voyageNo')}
          {text('port')}
          <DayInput
            label={f('date')}
            value={state.header.date}
            onChange={(date) => setHeader({ date })}
          />
          {text('flag')}
          {text('lastPort')}
        </div>
      </Panel>
      <Panel>
        <div className={classes.bar}>
          <div>
            <strong>{e('crew')}</strong>
            <div className={classes.muted}>{e('crewHint')}</div>
          </div>
          <Group gap="xs">
            <span className={classes.muted}>
              {e('selected', { count: selected, total: state.rows.length })}
            </span>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => onChange({ ...state, rows: sortRows(state.rows, i18n.language) })}
            >
              {a('sortByRank')}
            </Button>
          </Group>
        </div>
        {state.rows.length === 0 ? (
          <EmptyState icon={<IconListDetails size={22} />} title={e('noCrew')} />
        ) : (
          <div className={table.tableBox}>
            <table className={table.table} data-cards data-testid="crew-list-editor-rows">
              <thead>
                <tr>
                  <th scope="col">
                    <Checkbox
                      aria-label={a('selectAll')}
                      checked={allSelected}
                      indeterminate={selected > 0 && !allSelected}
                      onChange={() =>
                        onChange({
                          ...state,
                          rows: state.rows.map((row) => ({ ...row, include: !allSelected })),
                        })
                      }
                    />
                  </th>
                  <th scope="col">{f('name')}</th>
                  <th scope="col">{f('rank')}</th>
                  <th scope="col">{f('nationality')}</th>
                  <th scope="col">{f('birthDate')}</th>
                  <th scope="col">{f('birthPlace')}</th>
                  <th scope="col">{f('document')}</th>
                  <th scope="col">
                    <VisuallyHidden>{t('crewList.columns.actions')}</VisuallyHidden>
                  </th>
                </tr>
              </thead>
              <tbody className={own.rows}>
                {state.rows.map((row, index) => {
                  const name = crewListName(row);
                  return (
                    <tr key={row.userId} data-excluded={!row.include || undefined}>
                      <td>
                        <Checkbox
                          aria-label={a('select', { name })}
                          checked={row.include}
                          onChange={() => setRow(index, { ...row, include: !row.include })}
                        />
                      </td>
                      <td data-label={f('name')}>
                        <strong>{name}</strong>
                      </td>
                      <td data-label={f('rank')}>{row.rank || '—'}</td>
                      <td data-label={f('nationality')}>
                        <TextInput
                          size="xs"
                          aria-label={`${f('nationality')} · ${name}`}
                          value={row.nationality}
                          onChange={(event) =>
                            setRow(index, { ...row, nationality: event.currentTarget.value })
                          }
                        />
                        {edited(row, 'nationality')}
                      </td>
                      <td data-label={f('birthDate')}>
                        <DayInput
                          size="xs"
                          aria-label={`${f('birthDate')} · ${name}`}
                          value={row.birthDate}
                          onChange={(birthDate) => setRow(index, { ...row, birthDate })}
                        />
                        {edited(row, 'birthDate')}
                      </td>
                      <td data-label={f('birthPlace')}>
                        <TextInput
                          size="xs"
                          aria-label={`${f('birthPlace')} · ${name}`}
                          value={row.birthPlace}
                          onChange={(event) =>
                            setRow(index, { ...row, birthPlace: event.currentTarget.value })
                          }
                        />
                        {edited(row, 'birthPlace')}
                      </td>
                      <td data-label={f('document')}>
                        {draft.twoDocuments ? (
                          // Kutu 11: pasaport ve cüzdan birlikte seçilebilir (04.11 notları,
                          // karar 13); eski gemi sunucusunda tek belge seçimi kalır
                          <div className={own.docs}>
                            {DOC_TYPES.map((type) => {
                              const doc = selectedDocs(row).find((item) => item.type === type);
                              const typeName = t(`crewList.docType.${type}`);
                              return (
                                <Group key={type} gap={4} wrap="nowrap">
                                  <Checkbox
                                    size="xs"
                                    label={typeName}
                                    aria-label={`${typeName} · ${name}`}
                                    checked={doc !== undefined}
                                    onChange={(event) =>
                                      setRow(
                                        index,
                                        withDocSelection(row, type, event.currentTarget.checked),
                                      )
                                    }
                                    w={130}
                                  />
                                  {doc ? (
                                    <TextInput
                                      size="xs"
                                      aria-label={`${f('docNumber')} · ${typeName} · ${name}`}
                                      value={doc.number}
                                      onChange={(event) =>
                                        setRow(
                                          index,
                                          withDocNumber(row, type, event.currentTarget.value),
                                        )
                                      }
                                      w={130}
                                    />
                                  ) : null}
                                </Group>
                              );
                            })}
                          </div>
                        ) : (
                          <Group gap={4} wrap="nowrap">
                            <Select
                              size="xs"
                              aria-label={`${e('docType')} · ${name}`}
                              data={docTypes}
                              value={row.docType}
                              allowDeselect={false}
                              onChange={(value) =>
                                value && setRow(index, withDocType(row, value as CrewListDocType))
                              }
                              w={150}
                            />
                            <TextInput
                              size="xs"
                              aria-label={`${f('docNumber')} · ${name}`}
                              value={row.docNumber}
                              onChange={(event) =>
                                setRow(index, { ...row, docNumber: event.currentTarget.value })
                              }
                              w={130}
                            />
                          </Group>
                        )}
                      </td>
                      <td>
                        <Group gap={2} wrap="nowrap">
                          <ActionIcon
                            variant="subtle"
                            aria-label={a('moveUp', { name })}
                            disabled={index === 0}
                            onClick={() =>
                              onChange({ ...state, rows: moveRow(state.rows, index, -1) })
                            }
                          >
                            <IconArrowUp size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            aria-label={a('moveDown', { name })}
                            disabled={index === state.rows.length - 1}
                            onClick={() =>
                              onChange({ ...state, rows: moveRow(state.rows, index, 1) })
                            }
                          >
                            <IconArrowDown size={16} />
                          </ActionIcon>
                        </Group>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className={`${classes.muted} ${own.notes}`}>
          <span>{e('savedNote')}</span>
          <span>{e('noPassengers')}</span>
        </div>
        {error ? (
          <Alert color="red" mt="sm">
            {error}
          </Alert>
        ) : null}
        <Group justify="flex-end" gap="xs" mt="sm">
          <Button variant="default" onClick={onCancel} disabled={busy}>
            {a('cancel')}
          </Button>
          <Button
            variant="light"
            leftSection={<IconPrinter size={16} />}
            loading={busy}
            onClick={() => void save(true)}
          >
            {a('savePrint')}
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            loading={busy}
            onClick={() => void save(false)}
          >
            {a('save')}
          </Button>
        </Group>
      </Panel>
    </>
  );
}
